<template>
    <v-container class="mt-3">
        <!-- Error banner -->
        <v-card v-if="errors.length" class="mb-3">
            <v-card-text>
                <v-alert class="bg-error" closable close-label="Close Alert" density="compact"
                         variant="tonal">
                    <p v-for="(error, idx) in errors" :key="idx">{{ error }}</p>
                </v-alert>
            </v-card-text>
        </v-card>

        <!-- Welcome card -->
        <div v-if="showingWelcome && currentDoc" class="d-flex justify-center align-center" style="min-height: 60vh;">
            <v-card max-width="500" class="text-center pa-8" elevation="2" rounded="lg">
                <v-avatar :color="docIconColor" size="64" class="mb-4">
                    <v-icon size="32" color="white">{{ docIcon }}</v-icon>
                </v-avatar>

                <h4 class="text-h5 font-weight-bold mb-1">{{ currentDoc.title }}</h4>
                <p v-if="docSubtitle" class="text-body-2 text-grey-darken-1 mb-3">{{ docSubtitle }}</p>

                <p v-if="currentDoc.description" class="text-body-1 text-grey-darken-2 mb-4" style="line-height: 1.6;">
                    {{ currentDoc.description }}
                </p>

                <!-- Progress (multi-doc) -->
                <div v-if="totalDocs > 1" class="mb-5">
                    <p class="text-body-2 text-grey-darken-1 mb-2">Document {{ currentDocIndex + 1 }} of {{ totalDocs }}</p>
                    <div class="d-flex justify-center ga-2">
                        <div v-for="i in totalDocs" :key="i"
                             :style="{
                                 width: '10px', height: '10px', borderRadius: '50%',
                                 backgroundColor: i <= currentDocIndex + 1 ? '#6B46C1' : undefined,
                                 border: i > currentDocIndex + 1 ? '2px solid #BDBDBD' : undefined
                             }"
                        />
                    </div>
                </div>

                <v-btn color="primary" size="large" block @click="showingWelcome = false">
                    Review Document
                    <v-icon end>mdi-arrow-right</v-icon>
                </v-btn>

                <v-btn variant="text" class="mt-3" color="grey" @click="$emit('logout')">Logout</v-btn>
            </v-card>
        </div>

        <!-- PDF viewer -->
        <template v-else>
            <v-card class="mb-2">
                <v-card-title class="d-flex justify-space-between align-center">
                    <div>
                        <span v-if="totalDocs > 1" class="text-body-2 text-grey-darken-1">
                            Document {{ currentDocIndex + 1 }} of {{ totalDocs }}
                        </span>
                    </div>
                    <v-btn variant="outlined" size="small" @click="$emit('logout')">Logout</v-btn>
                </v-card-title>
            </v-card>
            <v-row>
                <v-col cols="12">
                    <!--
                      HTML documents reflow to the viewport and scroll
                      continuously; PDFs keep the paged canvas viewer, because a
                      fixed page layout has nothing to reflow to. Both emit the
                      same sign-action payload.
                    -->
                    <HtmlDocViewer
                        v-if="!processing && currentDoc?.pdf && isHtmlDoc"
                        :key="`html-${currentDoc?.id}`"
                        :src="currentDoc.pdf"
                        :title="`${currentDoc.title} - ${currentDoc.version}`"
                        height="70vh"
                        @sign-action="signDocument"
                    />
                    <PDFCanvasViewer
                        v-else-if="!processing && currentDoc?.pdf"
                        :key="currentDoc?.id"
                        :scale="1.25"
                        :src="currentDoc.pdf"
                        :title="`${currentDoc.title} - ${currentDoc.version}`"
                        height="70vh"
                        @sign-action="signDocument"
                    />
                    <v-progress-circular v-else-if="!currentDoc?.pdf && !pdfError" color="primary" indeterminate/>
                    <div v-else-if="pdfError" class="text-center pa-8">
                        <v-icon color="error" size="48">mdi-file-alert-outline</v-icon>
                        <p class="mt-4 text-body-1">Failed to load document</p>
                        <v-btn class="mt-4" color="primary" variant="outlined" @click="retryDocument">Retry</v-btn>
                    </div>
                </v-col>
            </v-row>
        </template>

        <!-- Decline confirmation dialog -->
        <v-dialog v-model="showDeclineDialog" max-width="440" persistent>
            <v-card class="pa-4">
                <v-card-title class="text-h6">Are you sure?</v-card-title>
                <v-card-text>
                    Declining {{ currentDoc?.title ? `"${currentDoc.title}"` : 'this document' }}
                    will prevent access to your account. You can accept later by signing in again.
                </v-card-text>
                <v-card-actions class="justify-end">
                    <v-btn variant="outlined" @click="showDeclineDialog = false">Go Back</v-btn>
                    <v-btn color="error" @click="confirmDecline">Decline &amp; Log Out</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script setup>
import PDFCanvasViewer from '@/components/cards/PdfViewer.vue';
import HtmlDocViewer from '@/components/cards/HtmlDocViewer.vue';
import {useSnackbarStore} from "@/stores/snackBarStore";
import getDeviceName from '@/assets/js/device-info';
import {send} from "@/composables/apiClient";
import {onMounted, onBeforeUnmount, ref, computed} from "vue";
import router from "@/router";
import {formatEffectiveMonthYear} from '@/composables/calendarDate';

const props = defineProps(['docs']);
const {showSnackbar} = useSnackbarStore();
const docs = ref(props.docs ?? []);
const initialDocCount = props.docs?.length ?? 0;
const deviceDetails = ref(null);
const currentDocId = ref(null);
const processing = ref(false);
const currentDoc = ref(null);
// Which viewer to use, as reported by the server. Not sniffed from the signed
// URL, which carries no extension. Anything unrecognised reads as PDF, the
// format every document used before HTML was allowed.
const isHtmlDoc = computed(() => String(currentDoc.value?.format ?? '').toLowerCase() === 'html');
const ipInfo = ref(null);
const errors = ref([]);
const pdfError = ref(false);
const showingWelcome = ref(true);
const showDeclineDialog = ref(false);

const emits = defineEmits(['updateConsents', 'logout']);

const totalDocs = initialDocCount;
const currentDocIndex = computed(() => totalDocs - docs.value.length);

const docIcon = computed(() => {
    const key = (currentDoc.value?.doc_key ?? '').toLowerCase();
    if (key.includes('terms') || key.includes('tos')) return 'mdi-gavel';
    if (key.includes('privacy')) return 'mdi-lock-outline';
    if (key.includes('eula')) return 'mdi-file-sign';
    return 'mdi-file-document-outline';
});

const docIconColor = computed(() => {
    const key = (currentDoc.value?.doc_key ?? '').toLowerCase();
    if (key.includes('terms') || key.includes('tos')) return '#6B46C1';
    if (key.includes('privacy')) return '#0284C7';
    if (key.includes('eula')) return '#059669';
    return '#6B46C1';
});

const docSubtitle = computed(() => {
    const parts = [];
    if (currentDoc.value?.version) parts.push(`Version ${currentDoc.value.version}`);
    if (currentDoc.value?.effective_date) {
        try {
            const effective = formatEffectiveMonthYear(currentDoc.value.effective_date);
            if (effective) parts.push(`Effective ${effective}`);
        } catch { /* ignore */ }
    }
    return parts.join(' · ');
});

onMounted(async () => {
    await getDocument();
    await getIpGeo();
    deviceDetails.value = getDeviceName()
})
const getDocument = async () => {
    if (!docs.value?.length) {
        showingWelcome.value = false;
        emits('updateConsents', null);
        // Straight to the dashboard. There used to be an "All Set!" card here
        // first, which said nothing the user had not just done - and because
        // clearing consents releases the parent gate and tears this component
        // down, it only ever flashed on the way past anyway.
        router.push({name: 'dashboard'});
        return;
    }
    pdfError.value = false;
    showingWelcome.value = true;
    currentDocId.value = docs.value[0] ?? null;
    if (currentDocId.value) {
        try {
            const response = await send('get', `legal-document-details/${currentDocId.value}`);
            if (response.data.success) {
                currentDoc.value = response.data.data;
                await getPdf(currentDoc.value.url);
            } else {
                pdfError.value = true;
                showSnackbar('Unable to load document', 'error');
            }
        } catch (e) {
            pdfError.value = true;
            showSnackbar('Failed to fetch document', 'error');
        }
    }
};
const retryDocument = () => {
    pdfError.value = false;
    if (currentDoc.value?.pdf) URL.revokeObjectURL(currentDoc.value.pdf);
    currentDoc.value = null;
    getDocument();
};
const getPdf = async (pdfUrl) => {
    try {
        const response = await send('get', pdfUrl, null, {
            responseType: 'blob',
        });
        if (response.status === 200 && response.data?.size > 0) {
            const pdfBlob = new Blob([response.data], {
                // Must match the document: a text/html payload handed to the
                // browser as application/pdf renders as nothing at all.
                type: isHtmlDoc.value ? 'text/html;charset=utf-8' : 'application/pdf',
            });
            currentDoc.value.pdf = window.URL.createObjectURL(pdfBlob);
        } else {
            pdfError.value = true;
        }
    } catch {
        pdfError.value = true;
    }
}
const getIpGeo = async () => {
    try {
        const res = await fetch('https://free.freeipapi.com/api/json');
        ipInfo.value = await res.json();
    } catch {
        ipInfo.value = null;
    }
};

const confirmDecline = async () => {
    showDeclineDialog.value = false;
    await signDocument({accept: 0, scrolled_to_bottom: false, time_spent: 0}, true);
    emits('logout');
};

const signDocument = (feedback, skipDeclineCheck = false) => {
    // Intercept decline - show confirmation dialog
    if (feedback.accept === 0 && !skipDeclineCheck) {
        showDeclineDialog.value = true;
        return;
    }

    errors.value = [];
    processing.value = true;
    const data = {...feedback, device_details: deviceDetails.value, ip_info: ipInfo.value}
    return send('post', `legal-document-sign/${currentDocId.value}`, data)
        .then((response) => {
            if (response.data.success) {
                docs.value = response.data.data;
                emits('updateConsents', response.data.data);
                if (currentDoc.value?.pdf) URL.revokeObjectURL(currentDoc.value.pdf);
                currentDoc.value = null;
                getDocument()
            }
        })
        .catch(function (errorResponse) {
            if (errorResponse.response?.data?.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response?.data?.message) {
                errors.value.push(errorResponse.response.data.message);
            }
            showSnackbar('Something went wrong', 'error');
        })
        .finally(() => processing.value = false);
}

onBeforeUnmount(() => {
    if (currentDoc.value?.pdf) {
        URL.revokeObjectURL(currentDoc.value.pdf);
    }
});
</script>
